import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiBook, FiHelpCircle, FiPlay, FiArrowRight, FiBarChart2 } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { BRAND, GOLD_GRADIENT, IVORY_GRADIENT_TEXT, Page, Kicker, Card, Stat, ProgressBar, Tag, fadeUp, stagger, deckMeta, CAST } from '../common/ui';

const HERO = { img: '/illustrations/lesson1/scenes/trace-studio.jpg', pos: 'center 30%', height: 640, opacity: 0.6 };

const SAY = [
  "Welcome back. Every trap has a tell — today we learn to spot one more.",
  "Good to see you. The best defenders are the ones who keep showing up.",
  "Ready? Pick a lesson, earn a badge, and keep that streak alive.",
  "Jason and Jackie never rest. Luckily, neither do we.",
];

function lessonId(lp) { if (!lp?.lesson) return null; return typeof lp.lesson === 'object' ? lp.lesson._id : lp.lesson; }

export default function Dashboard() {
  const { user } = useAuth();
  const name = user?.displayName || user?.name || 'Student';
  const firstName = name.split(/\s+/)[0];
  const courseId = user?.course?._id || user?.course;

  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [l, p] = await Promise.all([
          courseId ? api.get(`/lessons/course/${courseId}`) : Promise.resolve({ data: { lessons: [] } }),
          api.get('/progress/my'),
        ]);
        if (!cancelled) { setLessons(l.data?.lessons || []); setProgress(p.data?.progress || null); }
      } catch (e) { /* dashboard degrades gracefully */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [courseId]);

  const stats = useMemo(() => {
    const entries = progress?.lessons || [];
    const doneIds = new Set(entries.filter(e => e.completed).map(e => String(lessonId(e))));
    const startedIds = new Set(entries.filter(e => !e.completed && (e.timeSpent > 0)).map(e => String(lessonId(e))));
    const next = lessons.find(l => !doneIds.has(String(l._id))) || lessons[lessons.length - 1] || null;
    const nextIndex = next ? lessons.indexOf(next) : -1;
    return {
      done: doneIds.size,
      total: lessons.length,
      badges: progress?.badges?.length || 0,
      points: progress?.totalPoints || 0,
      streak: progress?.streak || 0,
      next, nextIndex, resuming: next ? startedIds.has(String(next._id)) : false,
    };
  }, [progress, lessons]);

  const say = SAY[(name.length + new Date().getDate()) % SAY.length];
  const pct = stats.total ? Math.round((stats.done / stats.total) * 100) : 0;
  const nextMeta = stats.next ? deckMeta(stats.next) : null;
  const host = CAST[nextMeta?.host] || CAST.trace;

  return (
    <Page hero={HERO}>
      {/* hero */}
      <motion.section variants={fadeUp} initial="hidden" animate="visible"
        style={{ position: 'relative', borderRadius: 28, overflow: 'hidden', minHeight: 320, border: `1px solid ${BRAND.line}`, boxShadow: '0 24px 70px rgba(0,0,0,0.45)', background: BRAND.navy800 }}>
        <img src={BRAND.bg.classroom} alt="" aria-hidden style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 35%' }} />
        <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(7,20,45,0.96) 0%, rgba(7,20,45,0.86) 48%, rgba(7,20,45,0.3) 100%)' }} />
        <div style={{ position: 'relative', zIndex: 1, padding: 'clamp(24px,3.5vw,44px)', maxWidth: 620 }}>
          <Kicker>Student dashboard</Kicker>
          <h1 style={{ fontSize: 'clamp(2rem,4vw,3rem)', fontWeight: 900, lineHeight: 1.05, margin: '10px 0 14px', ...IVORY_GRADIENT_TEXT }}>Hi {firstName}, welcome back.</h1>

          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 22 }}>
            <img src={CAST.trace.img} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: `2px solid ${BRAND.gold}`, background: BRAND.navy700, flexShrink: 0 }} />
            <div style={{ background: 'rgba(249,240,231,0.08)', border: `1px solid ${BRAND.line}`, borderRadius: '4px 18px 18px 18px', padding: '10px 14px' }}>
              <div style={{ fontSize: '0.7rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: BRAND.goldLight, fontWeight: 800 }}>Trace</div>
              <p style={{ fontFamily: BRAND.serif, fontStyle: 'italic', fontSize: '1.15rem', lineHeight: 1.3, margin: '2px 0 0', color: 'rgba(249,240,231,0.92)' }}>"{say}"</p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            {stats.next ? (
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Link to={`/lezioni/${stats.next._id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '14px 26px', borderRadius: 999, background: GOLD_GRADIENT, color: BRAND.navy900, fontWeight: 900, fontSize: '1rem', boxShadow: '0 10px 30px rgba(205,155,80,0.4)' }}>
                  {stats.resuming ? 'Resume' : stats.done >= stats.total && stats.total ? 'Replay'  : 'Start'} lesson {stats.nextIndex + 1} <FiArrowRight size={18} />
                </Link>
              </motion.div>
            ) : (
              <Link to="/lezioni" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '14px 26px', borderRadius: 999, background: GOLD_GRADIENT, color: BRAND.navy900, fontWeight: 900 }}>Open the lessons <FiArrowRight size={18} /></Link>
            )}
            {stats.next && (
              <span style={{ fontSize: '0.9rem', color: BRAND.muted, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <img src={host.img} alt="" style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: `1px solid ${BRAND.gold}` }} />
                <span style={{ fontWeight: 700, color: BRAND.ivory, maxWidth: 'min(260px, 50vw)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stats.next.title}</span>
                <span style={{ whiteSpace: 'nowrap' }}>with {host.name}</span>
              </span>
            )}
          </div>
        </div>
      </motion.section>

      {/* stats */}
      <motion.div variants={stagger} initial="hidden" animate="visible" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14, marginTop: 20 }}>
        <motion.div variants={fadeUp}>
          <Stat icon="📘" value={loading ? '…' : `${stats.done}/${stats.total}`} label="lessons completed">
            <ProgressBar value={pct} height={4} style={{ marginTop: 8 }} />
          </Stat>
        </motion.div>
        <motion.div variants={fadeUp}><Stat icon="🏅" value={loading ? '…' : stats.badges} label="badges earned" /></motion.div>
        <motion.div variants={fadeUp}><Stat icon="⭐" value={loading ? '…' : stats.points} label="points" /></motion.div>
        <motion.div variants={fadeUp}><Stat icon="🔥" value={loading ? '…' : `${stats.streak} ${stats.streak === 1 ? 'day' : 'days'}`} label="streak" /></motion.div>
      </motion.div>

      {/* actions */}
      <motion.div variants={stagger} initial="hidden" animate="visible" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginTop: 24 }}>
        {[
          { to: '/lezioni', n: '01', title: 'Lessons', sub: `${stats.total || 18} cinematic missions with Trace and the team.`, icon: FiBook, img: BRAND.bg.classroom },
          { to: '/quiz', n: '02', title: 'Quiz', sub: 'Test what you learned and collect points.', icon: FiHelpCircle, img: BRAND.bg.lab },
          { to: '/giochi', n: '03', title: 'Games', sub: 'Train your reflexes against Jason and Jackie.', icon: FiPlay, img: BRAND.bg.school },
          { to: '/statistiche', n: '04', title: 'My stats', sub: 'Badges, streak and your place on the leaderboard.', icon: FiBarChart2, img: null },
        ].map(a => (
          <motion.div key={a.to} variants={fadeUp} whileHover={{ y: -5 }} whileTap={{ scale: 0.99 }}>
            <Link to={a.to} style={{ display: 'block', height: '100%' }}>
              <Card style={{ position: 'relative', overflow: 'hidden', height: '100%', minHeight: 170, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: 22 }}>
                {a.img && <img src={a.img} alt="" aria-hidden style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.22 }} />}
                <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(11,26,58,0.1), rgba(11,26,58,0.95) 70%)' }} />
                <span aria-hidden style={{ position: 'absolute', top: 10, right: 16, fontSize: '3.4rem', fontWeight: 900, color: 'rgba(205,155,80,0.16)', lineHeight: 1 }}>{a.n}</span>
                <div style={{ position: 'relative' }}>
                  <span style={{ width: 40, height: 40, borderRadius: 12, background: GOLD_GRADIENT, color: BRAND.navy900, display: 'grid', placeItems: 'center', marginBottom: 12 }}><a.icon size={20} /></span>
                  <h3 style={{ fontSize: '1.3rem', fontWeight: 900, margin: 0 }}>{a.title}</h3>
                  <p style={{ fontSize: '0.9rem', color: BRAND.muted, margin: '4px 0 0', lineHeight: 1.4 }}>{a.sub}</p>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, color: BRAND.goldLight, fontWeight: 800, fontSize: '0.86rem' }}>Open <FiArrowRight size={14} /></span>
                </div>
              </Card>
            </Link>
          </motion.div>
        ))}
      </motion.div>

      {/* recent badges */}
      {!!(progress?.badges?.length) && (
        <motion.section variants={fadeUp} initial="hidden" animate="visible" style={{ marginTop: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
            <h2 style={{ fontSize: '1.2rem', fontWeight: 900, margin: 0 }}>Latest badges</h2>
            <Link to="/statistiche" style={{ color: BRAND.goldLight, fontWeight: 800, fontSize: '0.86rem' }}>See all →</Link>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {[...progress.badges].sort((a, b) => new Date(b.earnedAt || 0) - new Date(a.earnedAt || 0)).slice(0, 8).map(b => (
              <Card key={b.id} style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: '1.4rem' }}>{b.icon || '🏅'}</span>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '0.9rem' }}>{b.name}</div>
                  {b.desc && <div style={{ fontSize: '0.74rem', color: BRAND.muted }}>{b.desc}</div>}
                </div>
              </Card>
            ))}
            {progress.badges.length > 8 && <Tag tone="muted" style={{ alignSelf: 'center' }}>+{progress.badges.length - 8} more</Tag>}
          </div>
        </motion.section>
      )}
    </Page>
  );
}
